import styles from "./Us30Page.module.css";
import videoPlaceholderImage from "../../assets/images/video-player-placeholder.png";
import api from "../../services/api";
import { ENDPOINTS } from "../../utils/endpoints";
import { useState, useEffect, useRef } from "react";
import useDashboardStore from "../../store/dashboardStore";
import { FaSpinner } from "react-icons/fa";
import useAuthStore from "../../store/authStore";
import { toast } from "react-toastify";

const Us30Page = () => {
  const { setIsZoomMeetingScreen, setSelectedZoomSession } =
    useDashboardStore();
  const scrollRef = useRef(null);
  const [us30, setUs30] = useState(null);
  const [us30Content, setUs30Content] = useState(null);
  const [sortedVideos, setSortedVideos] = useState([]);
  const [timeLeft, setTimeLeft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPopup, setShowPopup] = useState(false);
  const [thumbnailCache, setThumbnailCache] = useState({});
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);

  // 🔐 Membership purchase states
  const { user, userMeta } = useAuthStore();
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [showPlanSelection, setShowPlanSelection] = useState(false);
  const [availablePlans, setAvailablePlans] = useState([]);

  const role = user?.roles?.[0];
  let userMembershipIds = [];
  if (Array.isArray(userMeta?.membership_ids)) {
    userMembershipIds = userMeta.membership_ids.map(Number);
  } else if (typeof userMeta?.membership_ids === "string") {
    try {
      userMembershipIds = JSON.parse(userMeta.membership_ids).map(Number);
    } catch {
      userMembershipIds = [];
    }
  }

  const scrollLeft = () => {
    scrollRef.current.scrollBy({ left: -600, behavior: "smooth" });
  };

  const scrollRight = () => {
    scrollRef.current.scrollBy({ left: 600, behavior: "smooth" });
  };

  // Fetch US30 session data
  useEffect(() => {
    const fetchUS30Data = async () => {
      try {
        const { data } = await api.get(ENDPOINTS.GET_US30_LIVE_SESSION);
        if (data.success) {
          setUs30(data.session);
        }
      } catch (err) {
        console.error("Failed to fetch us30 data", err);
      }
    };

    fetchUS30Data();
  }, []);

  // Fetch US30 content data
  useEffect(() => {
    const fetchUS30Content = async () => {
      try {
        const { data } = await api.get(ENDPOINTS.GET_US30_CONTENT);
        if (data.success) {
          setUs30Content(data.content);

          // Sort videos immediately after fetching
          if (data.content.video_links) {
            const sorted = [...data.content.video_links].sort(
              (a, b) => (a.sort_order || 0) - (b.sort_order || 0)
            );
            setSortedVideos(sorted);
          }
        }
      } catch (err) {
        console.error("Failed to fetch us30 content", err);
      } finally {
        setLoading(false);
      }
    };

    fetchUS30Content();
  }, []);

  // Countdown timer
  useEffect(() => {
    if (!us30?.start_datetime) return;

    const calculateTimeLeft = () => {
      const difference = new Date(us30.start_datetime) - new Date();
      if (difference <= 0) return null;

      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / (1000 * 60)) % 60),
        seconds: Math.floor((difference / 1000) % 60),
      };
    };

    const timer = setInterval(() => {
      const updated = calculateTimeLeft();
      if (!updated) clearInterval(timer);
      setTimeLeft(updated);
    }, 1000);

    return () => clearInterval(timer);
  }, [us30]);

  // Preload video thumbnails
  useEffect(() => {
    if (!sortedVideos.length) return;

    const loadThumbnails = async () => {
      const cache = {};

      // Load all thumbnails in parallel
      const promises = sortedVideos.map(async (link, i) => {
        try {
          const thumb = await getVideoThumbnail(link.url, link.video_type);
          cache[i] = thumb || videoPlaceholderImage;
        } catch (error) {
          console.error(`Failed to load thumbnail for video ${i}:`, error);
          cache[i] = videoPlaceholderImage;
        }
      });

      await Promise.all(promises);
      setThumbnailCache(cache);
    };

    loadThumbnails();
  }, [sortedVideos]);

  function getYoutubeThumbnail(url) {
    const regex = /(?:youtube\.com\/.*v=|youtu\.be\/)([^&?/]+)/;
    const match = url.match(regex);
    if (match && match[1]) {
      return `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg`;
    }
    return null;
  }

  async function getVimeoThumbnail(url) {
    // Handle both public and private Vimeo URLs
    // Public: vimeo.com/123456789
    // Private: vimeo.com/123456789/abc123def456
    // With query params: vimeo.com/123456789/abc123def456?share=copy
    const regex = /vimeo\.com\/(\d+)(?:\/([a-zA-Z0-9]+))?/;
    const match = url.match(regex);

    if (!match || !match[1]) return null;

    const videoId = match[1];

    try {
      // Use the full URL for oEmbed API (works for both public and private)
      const response = await fetch(
        `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(url)}`
      );

      if (response.ok) {
        const data = await response.json();
        if (data.thumbnail_url) {
          return data.thumbnail_url
            .replace("_295x166", "_640x360")
            .replace("_640", "_1280");
        }
      }
    } catch (error) {
      console.error("Vimeo API error:", error);
    }

    // Fallback to vumbnail service
    return `https://vumbnail.com/${videoId}.jpg`;
  }

  async function getVideoThumbnail(url, videoType) {
    // Detect actual video type from URL, ignore the videoType parameter
    if (url.includes("vimeo.com")) {
      return await getVimeoThumbnail(url);
    } else if (url.includes("youtube.com") || url.includes("youtu.be")) {
      return getYoutubeThumbnail(url);
    }
    return videoPlaceholderImage;
  }

  /* ---------------- PURCHASE ---------------- */
  const handleMembershipClick = async (membershipIds) => {
    if (isPurchasing || isLoadingPlans) return;

    setIsLoadingPlans(true);

    try {
      if (!membershipIds?.length) {
        toast.error("No membership plans configured for this video");
        return;
      }

      if (membershipIds.length === 1) {
        handlePurchaseMembership(membershipIds[0]);
      } else {
        const { data } = await api.get(ENDPOINTS.GET_ALL_MEMBERSHIPS);
        const allPlans = data.memberships || [];

        const filteredPlans = allPlans.filter((plan) =>
          membershipIds.includes(plan.id)
        );

        setAvailablePlans(filteredPlans);
        setShowPlanSelection(true);
      }
    } catch (error) {
      console.error("Error loading membership plans:", error);
      toast.error("Failed to load membership plans");
    } finally {
      setIsLoadingPlans(false);
    }
  };

  const handlePurchaseMembership = async (membershipPlanId) => {
    if (!membershipPlanId || isPurchasing) return;

    setIsPurchasing(true);

    try {
      const response = await api.post(ENDPOINTS.PURCHASE_LINK, {
        user_id: user.id,
        lid: membershipPlanId,
      });

      if (response.data.success) {
        window.open(response.data.redirect_url, "_blank");
      } else {
        toast.error(
          response.data.message || "Failed to generate purchase link"
        );
      }
    } catch (error) {
      console.error("Purchase error:", error);
      toast.error("Failed to initiate purchase");
    } finally {
      setIsPurchasing(false);
    }
  };

  // const handleVideoClick = async (video) => {
  //   let hasAccess = true;

  //   if (video.membershipIds?.length && role !== "administrator") {
  //     hasAccess = video.membershipIds.some((id) =>
  //       userMembershipIds.includes(id)
  //     );
  //   }

  //   if (!hasAccess) {
  //     toast.error("Video is restricted");
  //     handleMembershipClick(video.membershipIds);
  //     return;
  //   }

  //   if (video.url.includes("vimeo")) {
  //     // Extract video ID and hash for private videos
  //     // Handles: vimeo.com/123456789 or vimeo.com/123456789/abc123def456
  //     const regex = /vimeo\.com\/(\d+)(?:\/([a-zA-Z0-9]+))?/;
  //     const match = video.url.match(regex);

  //     if (match && match[1]) {
  //       const videoId = match[1];
  //       const hash = match[2]; // Private video token (if exists)

  //       setSelectedVideo({
  //         type: "vimeo",
  //         id: videoId,
  //         hash: hash, // Store the hash for private videos
  //       });
  //       setShowVideoModal(true);
  //     }
  //   } else {
  //     window.open(video.url, "_blank", "noopener,noreferrer");
  //   }
  // };
  const handleVideoClick = async (video) => {
    let hasAccess = true;

    if (video.membershipIds?.length && role !== "administrator") {
      // ✅ Normalize video membership IDs to integers
      const normalizedVideoMembershipIds = (video.membershipIds || [])
        .map((id) => {
          const num = Number(id);
          return isNaN(num) ? 0 : num;
        })
        .filter((id) => id > 0);

      // ✅ User membership IDs are already normalized at the top of the component
      // (lines 33-42), so we can use them directly
      hasAccess = normalizedVideoMembershipIds.some((id) =>
        userMembershipIds.includes(id)
      );
    }

    if (!hasAccess) {
      toast.error("Video is restricted");
      handleMembershipClick(video.membershipIds);
      return;
    }

    if (video.url.includes("vimeo")) {
      // Extract video ID and hash for private videos
      const regex = /vimeo\.com\/(\d+)(?:\/([a-zA-Z0-9]+))?/;
      const match = video.url.match(regex);

      if (match && match[1]) {
        const videoId = match[1];
        const hash = match[2];

        setSelectedVideo({
          type: "vimeo",
          id: videoId,
          hash: hash,
        });
        setShowVideoModal(true);
      }
    } else {
      window.open(video.url, "_blank", "noopener,noreferrer");
    }
  };

  const handleJoinSession = () => {
    if (!us30) return;

    const now = new Date();
    const sessionStart = new Date(us30.start_datetime);
    const allowPlay = now >= sessionStart;

    if (allowPlay) {
      setIsZoomMeetingScreen();
      setSelectedZoomSession(us30);
    } else {
      setShowPopup(true);
    }
  };

  if (loading) {
    return (
      <div className={styles.mainContainer}>
        <div className={styles.appBar}>
          <h2 className={styles.title}>US30</h2>
        </div>
        <div className={styles.scrollableContent}>
          <div style={{ textAlign: "center", padding: "50px" }}>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.mainContainer}>
      <div className={styles.appBar}>
        <h2 className={styles.title}>US30</h2>
      </div>

      <div className={styles.scrollableContent}>
        {/* Banner Image */}
        {us30Content?.banner_image && (
          <img
            src={`${ENDPOINTS.IMAGE_BASE_URL}${us30Content.banner_image}`}
            className={styles.bannerImage}
            alt="US30 Banner"
          />
        )}

        {/* Title */}
        {us30Content?.title_text && (
          <h2 className={styles.subtitle}>{us30Content.title_text}</h2>
        )}

        {/* Description */}
        {us30Content?.description_text && (
          <div className={styles.descriptionContainer}>
            <h4 className={styles.description}>
              {us30Content.description_text}
            </h4>
          </div>
        )}

        {/* Founder Image and Secondary Text Container */}
        {(us30Content?.founder_image || us30Content?.secondary_text) && (
          <div className={styles.container}>
            {us30Content?.secondary_text && (
              <div className={styles.textSection}>
                <h4 className={styles.description}>
                  {us30Content.secondary_text}
                </h4>
              </div>
            )}
            {us30Content?.founder_image && (
              <div className={styles.imageSection}>
                <img
                  src={`${ENDPOINTS.IMAGE_BASE_URL}${us30Content.founder_image}`}
                  className={styles.introImage}
                  alt="Founder"
                />
              </div>
            )}
          </div>
        )}

        {/* Video Links */}
        {sortedVideos.length > 0 && (
          <div className={styles.videoSection}>
            <h2 className={styles.subtitle}>Watch videos to start</h2>
            <div className={styles.scrollContainer}>
              <button className={styles.arrow} onClick={scrollLeft}>
                ❮
              </button>
              <div className={styles.videoList} ref={scrollRef}>
                {sortedVideos.map((video, index) => {
                  const thumbnail =
                    thumbnailCache[index] || videoPlaceholderImage;

                  return (
                    <div
                      key={`${video.url}-${index}`}
                      className={styles.videoCardWrapper}
                      onClick={() => handleVideoClick(video)}
                    >
                      <img
                        src={thumbnail}
                        className={styles.videoCard}
                        alt={video.title || `Video ${index + 1}`}
                        onError={(e) => {
                          if (e.target.src !== videoPlaceholderImage) {
                            e.target.src = videoPlaceholderImage;
                          }
                        }}
                      />
                      <div className={styles.videoOverlay}>
                        <div className={styles.playIcon}>▶</div>
                      </div>
                      {video.title && (
                        <div className={styles.videoTitle}>{video.title}</div>
                      )}
                    </div>
                  );
                })}
              </div>
              <button className={styles.arrow} onClick={scrollRight}>
                ❯
              </button>
            </div>
          </div>
        )}

        {/* Live Session Section */}
        {us30 && (
          <div style={{ width: "100%" }}>
            <h2 className={styles.subtitle}>Join Live Session</h2>
            <button className={styles.submitBtn} onClick={handleJoinSession}>
              ▶
            </button>

            {timeLeft ? (
              <div className={styles.timerContainer}>
                <div className={styles.timerBlock}>
                  <div className={styles.timerValue}>{timeLeft.days}</div>
                  <div className={styles.timerLabel}>Days</div>
                </div>
                <div className={styles.timerBlock}>
                  <div className={styles.timerValue}>{timeLeft.hours}</div>
                  <div className={styles.timerLabel}>Hours</div>
                </div>
                <div className={styles.timerBlock}>
                  <div className={styles.timerValue}>{timeLeft.minutes}</div>
                  <div className={styles.timerLabel}>Minutes</div>
                </div>
                <div className={styles.timerBlock}>
                  <div className={styles.timerValue}>{timeLeft.seconds}</div>
                  <div className={styles.timerLabel}>Seconds</div>
                </div>
              </div>
            ) : (
              <div className={styles.sessionUnavailable}>Session started.</div>
            )}
          </div>
        )}
      </div>

      {/* Popup Modal */}
      {showPopup && (
        <div
          className={styles.popupOverlay}
          onClick={() => setShowPopup(false)}
        >
          <div
            className={styles.popupContent}
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Session Not Started Yet</h3>
            <p>Please wait for the session to start</p>
            <button
              className={styles.popupButton}
              onClick={() => setShowPopup(false)}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Video Modal for Vimeo */}
      {showVideoModal && selectedVideo && (
        <div
          className={styles.videoModalOverlay}
          onClick={() => {
            setShowVideoModal(false);
            setSelectedVideo(null);
          }}
        >
          <div
            className={styles.videoModalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className={styles.closeButton}
              onClick={() => {
                setShowVideoModal(false);
                setSelectedVideo(null);
              }}
            >
              ✕
            </button>
            {selectedVideo.type === "vimeo" && (
              <iframe
                src={
                  selectedVideo.hash
                    ? `https://player.vimeo.com/video/${selectedVideo.id}?h=${selectedVideo.hash}&autoplay=1&title=0&byline=0&portrait=0&controls=1`
                    : `https://player.vimeo.com/video/${selectedVideo.id}?autoplay=1&title=0&byline=0&portrait=0&controls=1`
                }
                className={styles.videoIframe}
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title="Vimeo Video"
              />
            )}
          </div>
        </div>
      )}

      {/* Plan Selection Modal */}
      {showPlanSelection && (
        <div className={styles.popupOverlay}>
          <div className={styles.popupContent}>
            <h3>Choose a Membership Plan</h3>

            {availablePlans.map((plan) => (
              <div key={plan.id} style={{ marginBottom: 16 }}>
                <h4>{plan.name || plan.label}</h4>
                <p>
                  {plan.payment_type === "free" ? "Free" : `$${plan.price}`}
                </p>
                <button
                  className={styles.popupButton}
                  disabled={isPurchasing}
                  onClick={() => {
                    handlePurchaseMembership(plan.id);
                    setShowPlanSelection(false);
                  }}
                >
                  {isPurchasing ? (
                    <>
                      <FaSpinner className="fa-spin" /> Processing...
                    </>
                  ) : (
                    "Select Plan"
                  )}
                </button>
              </div>
            ))}

            <button
              className={styles.popupButton}
              onClick={() => setShowPlanSelection(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Us30Page;
